import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later } from '@ember/runloop';
import Component from '@glimmer/component';

import $ from 'jquery';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import { themeClassNames } from 'webapp/models-custom/theme';

export enum IconPosition {
	BEFORE = 'before',
	AFTER = 'after',
}

export interface Args {
	onTrigger: (e: MouseEvent | TouchEvent) => void;
	text?: string;
	theme?: string;
	size?: string;
	ripplerEffect?: boolean;
	isDisabled?: boolean;
	submitInProgress?: boolean;
	noDelay?: boolean;
	tooltipText?: string;
	tooltipTheme?: string;
	extraClass?: string;
	icon?: IconName | '';
	iconPosition?: IconPosition;
	stopPropagation?: boolean;
}

export interface SubmitButtonComponentInterface<T> {
	Element: HTMLAnchorElement; // this is needed for ...attributes
	Args: T;
}

export default class SubmitButtonComponent extends Component<SubmitButtonComponentInterface<Args>> {
	get text(): string {
		return this.args.text ?? 'Save';
	}

	get ripplerEffect(): boolean {
		return this.args.isDisabled ? false : (this.args.ripplerEffect ?? true);
	}

	get theme(): string {
		return this.args.theme ?? 'blue';
	}

	get themeClass(): string {
		return themeClassNames(this.theme);
	}

	get size(): string {
		return this.args.size ?? 'normal';
	}

	get sizeClass(): string {
		return `size-${this.size}`;
	}

	get iconSize(): IconSize {
		switch (this.size) {
			case 'tiny':
			case 'small':
				return 'xs';
			case 'big':
				return 'md';
			case 'normal':
			default:
				return 'sm';
		}
	}

	get isIconAfter(): boolean {
		return this.args.iconPosition === IconPosition.AFTER;
	}

	@action
	setupRippler(node: HTMLElement): void {
		if (this.ripplerEffect) {
			$(node).rippler({
				effectClass: 'rippler-effect',
				effectSize: 0,
				addElement: 'div',
				duration: 200,
			});
		}
	}

	@action
	triggerAction(e: MouseEvent | TouchEvent): void {
		if (this.args.isDisabled || this.args.submitInProgress) {
			return;
		}

		if (this.args.noDelay) {
			this.args.onTrigger(e);
		} else {
			later(() => {
				this.args.onTrigger(e);
			}, 150);
		}

		if (this.args.stopPropagation) {
			e.stopPropagation();
		}

		e.preventDefault();
	}
	<template>
		<a
			...attributes
			class={{joinClasses
				(array
					"button"
					"rippler-default"
					this.themeClass
					this.sizeClass
					@extraClass
					(if @isDisabled "disabled")
					(if @submitInProgress "loading")
					(if this.ripplerEffect "rippler")
				)
			}}
			{{didInsert this.setupRippler}}
			{{on "click" this.triggerAction}}
			{{on "touchend" this.triggerAction}}
		>

			{{#if @icon}}
				{{#if this.isIconAfter}}
					{{{this.text}}}<CommonUiIconComponent @name={{@icon}} @size={{this.iconSize}} />
				{{else}}
					<CommonUiIconComponent @name={{@icon}} @size={{this.iconSize}} />{{{this.text}}}
				{{/if}}
			{{else}}
				<span>{{{this.text}}}</span>
			{{/if}}

			{{#if @submitInProgress}}
				<BarLoaderComponent />
			{{/if}}

			{{#if @tooltipText}}
				<ToolTipComponent @text={{@tooltipText}} @theme={{@tooltipTheme}} />
			{{/if}}
		</a>
	</template>
}
